/**
 * La scheda di un abbonamento sul telefono (7/10/2026): risponde a "questo
 * abbonamento sta pagando? quando si rinnova?".
 *
 * Prima, a 390px: il nome del piano, una riga di contesto che andava a capo,
 * quattro riquadri (ricavo, costo, margine, prossimo ciclo), poi la cronologia
 * e, in fondo, il modulo dei dati (CODICE PIANO / EXTERNAL ID…).
 * Qui:
 *   1. in grande, quanto incassa a ogni ciclo; sotto, ogni quanto e quando si
 *      rinnova (in rosso solo se il pagamento non è andato o è cancellato);
 *   2. il cliente (o il nome su Stripe, se non è in rubrica); lo sconto, se
 *      c'è; costo e margine per chi li può vedere;
 *   3. le fatture di pari importo, la cronologia con la nota veloce, i dati
 *      tecnici in fondo;
 *   4. in fondo "Sincronizza" e "Apri su Stripe", come prima.
 * Sospendere, riattivare e cancellare restano al computer (su Stripe sono
 * irreversibili): lo dice una riga.
 * Stesse chiamate e stessa cache della pagina del computer.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MANAGED_SERVICE_LABEL, SERVICE_STATUS_LABEL, type ManagedServiceType, type ServiceEventKind, type ServiceStatus } from '@masamune/shared';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { MCampo, MEmpty, MGroup, MHero, MMore, MPage, MRow, iniziali, leggibile } from '../../components/mobile/MobileKit';
import { fmtEur } from '../../lib/utils';
import { DueRighe, euro, euroRiga, giornoBreve } from '../admin/shared/AdminMobile';
import type { ServiceBilling } from './subscriptions.types';

interface Evento { id: string; kind: keyof typeof ServiceEventKind; message: string; createdAt: string }
interface AbbonamentoPieno {
  id: string;
  type: ManagedServiceType;
  provider: 'STRIPE' | 'HIGHLEVEL' | 'MANUAL';
  status: keyof typeof ServiceStatus;
  billingCycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  planCode: string;
  planLabel: string;
  amount: number;
  listAmount: number;
  costAmount: number;
  metadata: Record<string, unknown>;
  externalId: string | null;
  externalUrl: string | null;
  startedAt: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelledAt: string | null;
  lastSyncedAt: string | null;
  notes: string | null;
  client: { id: string; legalName: string; shortName: string } | null;
  events: Evento[];
}

const PROVIDER: Record<AbbonamentoPieno['provider'], string> = { STRIPE: 'Stripe', HIGHLEVEL: 'HighLevel', MANUAL: 'a mano' };
const CICLO: Record<AbbonamentoPieno['billingCycle'], string> = { MONTHLY: 'ogni mese', QUARTERLY: 'ogni 3 mesi', YEARLY: 'ogni anno' };
const ICONA_EVENTO: Partial<Record<keyof typeof ServiceEventKind, IconName>> = {
  CREATED: 'plus', ACTIVATED: 'check', PAUSED: 'arrowDown', RESUMED: 'arrowRight', CANCELLED: 'x',
  PAYMENT_OK: 'check', PAYMENT_FAILED: 'alert', PLAN_CHANGED: 'swap', RENEWED: 'repeat', SYNCED: 'refresh', NOTE: 'pencil',
};

export function AbbonamentoMobile({ id }: { id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((st) => st.user);
  const showMargin = !!user?.role.canSeeMargin;
  const { data: s, isLoading, isError } = useQuery({ queryKey: ['service', id], queryFn: () => api.get<AbbonamentoPieno>(`/services/${id}`), retry: false });
  // Le fatture che corrispondono a questo canone: query a parte, se fallisce la scheda resta in piedi (come sul computer).
  const { data: billing } = useQuery({ queryKey: ['service', id, 'billing'], queryFn: () => api.get<ServiceBilling>(`/services/${id}/billing`), retry: false });
  const [nota, setNota] = useState<string | null>(null);
  const [tuttiEventi, setTuttiEventi] = useState(false);

  const rinfresca = () => { qc.invalidateQueries({ queryKey: ['service', id] }); qc.invalidateQueries({ queryKey: ['services'] }); };
  const sync = useMutation({
    mutationFn: () => api.post(`/services/${id}/sync`),
    onSuccess: () => { toast.ok('Sincronizzazione completata'); rinfresca(); },
    onError: (err) => toast.alert('Errore sincronizzazione', err instanceof ApiError ? err.message : 'Riprova'),
  });
  const addNote = useMutation({
    mutationFn: (message: string) => api.post(`/services/${id}/events`, { kind: 'NOTE', message }),
    onSuccess: () => { toast.ok('Nota aggiunta'); setNota(null); qc.invalidateQueries({ queryKey: ['service', id] }); },
    onError: (err) => toast.alert('Errore', err instanceof ApiError ? err.message : 'Riprova'),
  });

  if (isError) return <MPage><MEmpty>Abbonamento non trovato: forse è stato cancellato, o il link è sbagliato.</MEmpty></MPage>;
  if (isLoading || !s) return <MPage><MEmpty>Carico l'abbonamento…</MEmpty></MPage>;

  const nomeStripe = (s.metadata?.stripeCustomerName as string | undefined) ?? null;
  const inProva = s.trialEndsAt && new Date(s.trialEndsAt) > new Date();
  const stato = s.status === 'PAST_DUE' ? <span className="is-alert">pagamento non riuscito</span>
    : s.status === 'CANCELLED' ? <span className="is-alert">cancellato{s.cancelledAt ? ` il ${giornoBreve(s.cancelledAt)}` : ''}</span>
    : s.status === 'EXPIRED' ? <span className="is-alert">scaduto</span>
    : s.status === 'PAUSED' ? 'in pausa'
    : inProva ? `in prova fino al ${giornoBreve(s.trialEndsAt)}`
    : s.currentPeriodEnd ? `si rinnova il ${giornoBreve(s.currentPeriodEnd)}` : SERVICE_STATUS_LABEL[s.status].toLowerCase();
  const scontato = s.listAmount > s.amount;
  const margine = s.amount - s.costAmount;
  const eventi = s.events;

  return (
    <>
      <MPage>
        <h1 className="sr-only">{s.planLabel}</h1>
        <MHero
          label={`${s.planLabel} · ${MANAGED_SERVICE_LABEL[s.type]}`}
          unit={s.amount > 0 ? '€' : undefined}
          value={s.amount > 0 ? fmtEur(s.amount) : 'Gratis'}
          sub={<>{CICLO[s.billingCycle]}, {PROVIDER[s.provider] === 'a mano' ? 'senza provider' : `su ${PROVIDER[s.provider]}`} · {stato}</>}
        />

        <MGroup title="Il cliente">
          {s.client ? (
            <MRow text={iniziali(s.client.legalName)} round title={leggibile(s.client.legalName)} sub="scheda del cliente" onClick={() => navigate(`/contatti/client/${s.client!.id}`)} />
          ) : (
            <MRow icon="alert" tone="alert" title={nomeStripe ? leggibile(nomeStripe) : 'Senza anagrafica'} sub="non è in rubrica: si collega dal computer" />
          )}
        </MGroup>

        {(scontato || showMargin) && (
          <MGroup title="Quanto ci resta" note={typeof s.metadata?.discountReason === 'string' ? `${s.metadata.discountReason as string}.` : undefined}>
            {scontato && (
              <MRow icon="percent" tone={s.amount === 0 ? 'alert' : undefined} title={s.amount === 0 ? 'Non incassa niente' : 'Sconto per sempre'}
                sub={`a listino ${euro(s.listAmount)} ${CICLO[s.billingCycle]}`} trail={euroRiga(s.listAmount - s.amount)} trailSub="in meno" />
            )}
            {showMargin && <MRow icon="coins" title="Costo del provider" trail={euroRiga(s.costAmount)} trailTone="mute" />}
            {showMargin && <MRow icon="chart" title="Margine" sub={s.amount > 0 ? `${((margine / s.amount) * 100).toFixed(0)}% del ricavo` : undefined} trail={euroRiga(margine)} />}
          </MGroup>
        )}

        {billing && (
          <MGroup title="Fatture di pari importo" count={billing.matched.length || undefined}
            note={billing.matched.length ? `Accoppiate per cliente e importo, non da un collegamento diretto.${billing.otherCount > 0 ? ` Altre ${billing.otherCount} fatture dello stesso cliente hanno importi diversi.` : ''}` : undefined}>
            {billing.reason === 'no_client' ? <MEmpty>Senza un cliente collegato non si possono cercare le fatture.</MEmpty>
              : billing.reason === 'zero_amount' ? <MEmpty>Il canone è zero: non c'è nessuna fattura da cercare.</MEmpty>
              : billing.matched.length === 0 ? <MEmpty>Nessuna fattura di questo importo: forse non è ancora fatturato, o è dentro un'altra fattura.</MEmpty>
              : billing.matched.map((f) => (
                <MRow key={f.id} icon="invoice" title={`Fattura ${f.number}`} sub={`${giornoBreve(f.issueDate)} · ${f.status.toLowerCase()}`} trail={euroRiga(f.total)} onClick={() => navigate(`/invoices/${f.id}`)} />
              ))}
          </MGroup>
        )}

        <MGroup title="Cronologia" count={eventi.length || undefined} action={{ label: 'Aggiungi una nota', onClick: () => setNota('') }}>
          {eventi.length === 0 ? <MEmpty>Sincronizzazioni, pagamenti e note compaiono qui.</MEmpty>
            : (tuttiEventi ? eventi : eventi.slice(0, 5)).map((e) => (
              <MRow key={e.id} icon={ICONA_EVENTO[e.kind] ?? 'clock'} tone={e.kind === 'PAYMENT_FAILED' || e.kind === 'CANCELLED' ? 'alert' : undefined}
                title={<DueRighe>{e.message}</DueRighe>} sub={giornoBreve(e.createdAt)} />
            ))}
          {eventi.length > 5 && <MMore label={tuttiEventi ? 'Mostra meno' : `Vedi tutti i ${eventi.length}`} onClick={() => setTuttiEventi((v) => !v)} />}
        </MGroup>

        <MGroup title="Dati tecnici" note="Sospendere, riattivare e cancellare si fanno dal computer: su Stripe non si torna indietro.">
          <MRow title="Codice del piano" trail={s.planCode} trailTone="mute" />
          {s.externalId && <MRow title="Id su Stripe" sub={s.externalId} />}
          <MRow title="Attivo dal" trail={giornoBreve(s.startedAt) || '—'} trailTone="mute" />
          <MRow title="Ultima sincronizzazione" trail={s.lastSyncedAt ? giornoBreve(s.lastSyncedAt) : 'mai'} trailTone="mute" />
          {s.notes && <MRow title="Note" sub={s.notes} subWrap />}
        </MGroup>
      </MPage>

      {/* In fondo, dove arriva il pollice: sincronizzare e aprire su Stripe (due al massimo, mobile.md §4). */}
      <div className="m-actionbar m-only">
        <button className="btn btn-primary" onClick={() => sync.mutate()} disabled={sync.isPending}>
          <Icon name="refresh" size={12} /> {sync.isPending ? 'Sincronizzo…' : 'Sincronizza'}
        </button>
        {s.externalUrl && (
          <a className="btn" href={s.externalUrl} target="_blank" rel="noreferrer">
            <Icon name="link" size={12} /> {PROVIDER[s.provider]}
          </a>
        )}
      </div>

      <MCampo
        open={nota !== null}
        onClose={() => setNota(null)}
        titolo="Nota sull'abbonamento"
        nota="Finisce nella cronologia, con la data di oggi."
        campo={<input className="inp" autoFocus value={nota ?? ''} onChange={(e) => setNota(e.target.value)} />}
        onSalva={() => { if (nota?.trim() && !addNote.isPending) addNote.mutate(nota.trim()); }}
      />
    </>
  );
}
